import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Users, Star, Target } from "lucide-react";

const TEAM_IMG = "https://customer-assets.emergentagent.com/job_98738aa4-d15a-49a5-91ed-b440272a52f8/artifacts/xl7t5wvb_362616623_763031139158765_2303536239581663007_n.jpg";
const STUDIO_IMG = "https://customer-assets.emergentagent.com/job_98738aa4-d15a-49a5-91ed-b440272a52f8/artifacts/ycwq5ecm_489609005_1216574520471089_8830070208935895582_n.jpg";

const values = [
  { icon: Award, title: "Excelenta", desc: "Fiecare mașină primește tratamentul de care are nevoie la cele mai înalte standarde." },
  { icon: Users, title: "Profesionalism", desc: "Echipa noastră este formată din specialiști certificați cu ani de experiență." },
  { icon: Star, title: "Calitate Premium", desc: "Folosim doar produse profesionale de top: Gtechniq, Crystal Serum Ultra." },
  { icon: Target, title: "Atentie la Detalii", desc: "Fiecare centimetru contează. Nu lăsăm nimic la voia întâmplării." },
];

export default function About() {
  return (
    <div className="pt-20 min-h-screen bg-black" data-testid="about-page">
      {/* Header */}
      <section className="py-20 page-header-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Despre <span className="text-red-600">Noi</span>
          </motion.h1>
          <motion.div
            className="red-line mx-auto"
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl mb-6">
              Povestea <span className="text-red-600">LSK</span>
            </h2>
            <div className="red-line mb-8" />
            <p className="text-white font-light leading-relaxed mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
              LSK Auto Styling Center s-a născut din pasiunea pentru automobilele frumoase și dorința
              de a oferi servicii de detailing la cele mai înalte standarde. Situat în Râmnicu Sărat,
              centrul nostru este dotat cu echipamente profesionale de ultimă generație.
            </p>
            <p className="text-white font-light leading-relaxed mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
              De-a lungul anilor, am tratat sute de vehicule premium - de la BMW și Mercedes
              până la Lamborghini, Ferrari și Bentley. Fiecare mașină primește un tratament
              personalizat, adaptat nevoilor specifice ale proprietarului.
            </p>
            <p className="text-white font-light leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Suntem certificați Gtechniq și folosim exclusiv produse premium Crystal Serum Ultra
              pentru a garanta rezultate excepționale și durabile.
            </p>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <img
              src={TEAM_IMG}
              alt="Echipa LSK"
              className="w-full aspect-square object-cover"
            />
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-red-600 -z-10" />
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4">
              Valorile <span className="text-red-600">Noastre</span>
            </h2>
            <div className="red-line mx-auto" />
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                className="shine-card p-8 bg-[#0A0A0A] border border-[#222] hover:border-red-600 transition-all duration-500 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`value-card-${i}`}
              >
                <val.icon className="w-12 h-12 text-red-600 mx-auto mb-6" />
                <h3 className="text-xl mb-3">{val.title}</h3>
                <p className="text-white font-light text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            className="relative order-2 md:order-1"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <img
              src={STUDIO_IMG}
              alt="Studio LSK"
              className="w-full aspect-[4/3] object-cover"
            />
            <div className="absolute -bottom-4 -left-4 w-full h-full border-2 border-red-600 -z-10" />
          </motion.div>

          <motion.div
            className="order-1 md:order-2"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl mb-6">
              Studio <span className="text-red-600">Profesional</span>
            </h2>
            <div className="red-line mb-8" />
            <p className="text-white font-light leading-relaxed mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Centrul nostru este echipat cu cele mai performante sisteme de iluminare hexagonală
              LED, care asigură vizibilitate perfectă pentru detectarea celor mai mici imperfecțiuni.
            </p>
            <p className="text-white font-light leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Lucrarea ta este efectuată într-un mediu controlat, protejat de praf și contaminanți,
              garantând rezultate impecabile.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-block px-8 py-3 bg-red-600 text-white hover:bg-red-700 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all duration-300 tracking-[0.15em] uppercase text-sm font-medium"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Programează-te Acum
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
